import React, { type PropsWithChildren } from 'react';

import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { useCopyToClipboard } from '@atlassian/search-ui/actions';
import { PersonCard, type PersonCardProps } from '@atlassian/search-ui/card';
import { MentionPopup } from '@atlassian/search-ui/mention-popup';

import { getExternalMessageDmIntegrationInfo } from '../../../utils/search-result-utils';
import type { mention_AtlassianSearchPage_UserMention$key } from './__generated__/mention_AtlassianSearchPage_UserMention.graphql';
import { messages } from './messages';

/**
 * The whitespace in between aliases <!here> <!channel> is removed
 * This constant prevent the alias from being pushed together ex: "@here@channel"
 * We are using this constant rather than adding a space through CSS because it adds
 * a real space character that does not break up the text when selecting.
 * */
const SPACE = ' ';

export const MentionText = ({ children }: PropsWithChildren<unknown>): React.JSX.Element => {
	return (
		<span>
			{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
			{SPACE}@{children}
		</span>
	);
};

export type UserMentionProps = {
	children?: string[];
	integrationId?: string | null;
	node: mention_AtlassianSearchPage_UserMention$key;
};

export const UserMention = ({
	children,
	integrationId,
	node,
}: UserMentionProps): React.JSX.Element => {
	const userId = children?.[0];
	let name;
	let entity;

	const data = useFragment(
		graphql`
			fragment mention_AtlassianSearchPage_UserMention on SearchResultGraphDocument {
				entity {
					... on ExternalMessage {
						externalId
						associatedWith {
							edges {
								node {
									entity {
										... on ThirdPartyUser {
											externalId
											name
											nickname
											email
											picture
											extendedProfile {
												jobTitle
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		node,
	);

	const userAssociation = data.entity?.associatedWith?.edges?.find(
		(edge) => Boolean(userId) && userId === edge?.node?.entity?.externalId,
	);

	entity = userAssociation?.node?.entity;
	name = entity?.nickname || entity?.name;

	const email = entity?.email;
	const copyToClipboard = useCopyToClipboard(email);

	if (entity && name) {
		type UserData = PersonCardProps['user'];
		// We are waiting for this commit ticket to complete for jobTitle & profileUrl
		// https://hello.jira.atlassian.cloud/browse/COMMIT-14774
		const user: UserData = {
			name,
			profilePicUrl: entity.picture,
			jobTitle: entity.extendedProfile?.jobTitle,
			profileUrl: null, // not available as of late Oct 2024
		};
		if (user) {
			return (
				<>
					{SPACE}
					<MentionPopup
						name={name}
						content={() => (
							<PersonCard
								onCopyEmail={copyToClipboard}
								user={user}
								externalMessageIntegration={getExternalMessageDmIntegrationInfo(
									integrationId,
									entity,
								)}
							/>
						)}
					/>
				</>
			);
		}
	}

	return (
		<MentionText>{name || <FormattedMessage {...messages.externalPersonLabel} />}</MentionText>
	);
};
